import { getTime } from 'date-fns';
import { first, isNil, last } from 'lodash';

import { DateRange } from '@/store';

export type SortOrder = 'asc' | 'desc';

/**
 * A run of rows, paired with the bounds it covers.
 *
 * Both a single window's answer and the series stitched from a set of them are this: rows, and the span
 * they were assembled under. The bounds come from the range that was asked for rather than from the rows,
 * so a window that came back empty still says what it covers.
 */
export type DataWindow<TItem> = { data: TItem[]; startMs: number; endMs: number };

/**
 * Drops the items sitting on or past `boundaryTs` from whichever end of `data` they are on. `data` must be
 * sorted on `timestampKey`, with `from` naming the end the boundary is at, so the scan can stop at the first
 * item inside the window.
 */
const trimBoundaryDatapoints = <TItem extends Record<TTimestampKey, string>, TTimestampKey extends string>(
    data: TItem[],
    timestampKey: TTimestampKey,
    boundaryTs: number | undefined,
    from: 'start' | 'end'
): TItem[] => {
    if (!data?.length || isNil(boundaryTs)) {
        return data;
    }
    const isOnBoundary = (item: TItem) => new Date(item[timestampKey]).getTime() >= boundaryTs;

    if (from === 'end') {
        let end = data.length;
        while (end > 0 && isOnBoundary(data[end - 1])) {
            end--;
        }
        return end === data.length ? data : data.slice(0, end);
    } else {
        let start = 0;
        while (start < data.length && isOnBoundary(data[start])) {
            start++;
        }
        return start === 0 ? data : data.slice(start);
    }
};

/**
 * Drops the items falling outside `[startMs, endMs]` from either end of `data`, which must be sorted on
 * `timestampKey`.
 *
 * Sorted either way round, the items outside the range are exactly a prefix and a suffix: ascending puts
 * the ones before `startMs` at the front, descending puts them at the back. So the sort direction does not
 * come into it, and each scan stops at the first item inside the range.
 *
 * Both bounds are inclusive, which is what keeps the item sitting exactly on a bound that an exclusive
 * endpoint was asked a millisecond either side of to fetch. `data` itself comes back when nothing falls
 * outside, so a series that needed no clipping keeps the identity the charts key their draws on.
 */
export const clipToRange = <TItem extends Record<TTimestampKey, string>, TTimestampKey extends string>(
    data: TItem[],
    timestampKey: TTimestampKey,
    startMs: number | undefined,
    endMs: number | undefined
): TItem[] => {
    if (!data?.length || (isNil(startMs) && isNil(endMs))) {
        return data;
    }
    const isOutside = (item: TItem) => {
        const ts = new Date(item[timestampKey]).getTime();
        return (!isNil(startMs) && ts < startMs) || (!isNil(endMs) && ts > endMs);
    };

    let lo = 0;
    let hi = data.length;
    while (lo < hi && isOutside(data[lo])) {
        lo++;
    }
    while (hi > lo && isOutside(data[hi - 1])) {
        hi--;
    }

    return lo === 0 && hi === data.length ? data : data.slice(lo, hi);
};

/** Pairs a window's answer with its own bounds, or `undefined` while that window has no answer yet. */
export const toDataWindow = <TItem>(
    range: DateRange | undefined,
    data: TItem[] | undefined
): DataWindow<TItem> | undefined =>
    range && data ? { data, startMs: range.startDate.getTime(), endMs: range.endDate.getTime() } : undefined;

/**
 * Concatenates a run of adjacent windows into one series, dropping the copies of a boundary item that the
 * windows on both sides of it returned.
 *
 * `windows` is in ascending time order and its members are adjacent, whichever direction `sortOrder` puts
 * the items inside them in. Each window is trimmed at its successor's start, which is the one instant both
 * of them cover: `dateRangeParams` widens a request by the millisecond an exclusive endpoint drops, so
 * every convention answers a window with exactly the instants between its own bounds.
 *
 * Nothing is re-sorted: each window is handed back in the order the endpoint returned it.
 */
export const stitchWindows = <TItem extends Record<TTimestampKey, string>, TTimestampKey extends string>(
    windows: DataWindow<TItem>[],
    timestampKey: TTimestampKey,
    sortOrder: SortOrder
): TItem[] => {
    const trimmed = windows.map(({ data }, index) => {
        const successor = windows[index + 1];
        return successor
            ? trimBoundaryDatapoints(data, timestampKey, successor.startMs, sortOrder === 'asc' ? 'end' : 'start')
            : data;
    });

    // a lone window is its own series, and keeps the array identity react-query already gave it
    if (trimmed.length === 1) {
        return trimmed[0];
    }

    const inSortOrder = sortOrder === 'asc' ? trimmed : trimmed.reverse();
    return ([] as TItem[]).concat(...inSortOrder);
};

/**
 * When the newest item in `series` is timestamped, or `undefined` for an empty series, one whose newest
 * item carries no timestamp, and one whose newest timestamp does not parse.
 *
 * `series` is sorted on `timestampKey` under `sortOrder`, so the newest is whichever end that puts it at.
 */
export const newestTimestampOf = <TItem extends Record<TTimestampKey, string>, TTimestampKey extends string>(
    series: TItem[],
    timestampKey: TTimestampKey,
    sortOrder: SortOrder
): number | undefined => {
    const newest = (sortOrder === 'asc' ? last(series) : first(series))?.[timestampKey];
    if (isNil(newest)) {
        return undefined;
    }

    const timestampMs = getTime(newest);
    return Number.isNaN(timestampMs) ? undefined : timestampMs;
};
